import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
const { Option } = Select;

interface LedgerItem {
    ledger_id: number;
    ledger_name: string;
}

interface ProductItem {
    product_id: number;
    product_name: string;
}

interface PIModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: any;
    isEditMode?: boolean;
}

const PIModel: React.FC<PIModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {

    const { ledgers, products, loaded } = useAppSelector(state => state.master);
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);


    console.log("ledgers", ledgers)

    /* ================= Submit ================= */
    const handleSubmit = async (values: any) => {
        try {
            const payload = {
                ...values,
                pi_date: values.pi_date.format('YYYY-MM-DD'),
                ...(initialValues?.pi_id && { pi_id: initialValues.pi_id }),
            };

            const url = isEditMode
                ? 'edit-performa-invoice'
                : 'add-performa-invoice';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: payload,
            });

            onCreate(data);

            showSuccess(
                isEditMode
                    ? 'Performa Invoice updated successfully!'
                    : 'Performa Invoice created successfully!'
            );

            form.resetFields();

            if (isModalClose) {
                onCancel();
            }

        } catch (error: any) {
            showError(
                error?.response?.data?.message ||
                `Failed to ${isEditMode ? 'update' : 'create'} invoice`
            );
        }
    };

    /* ================= Initial Values ================= */
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                ...initialValues,
                pi_date: initialValues.pi_date
                    ? dayjs(initialValues.pi_date)
                    : null,
                items: initialValues.items || [],
            });
        } else if (visible) {
            form.resetFields();
            form.setFieldsValue({
                items: [{ pid_product_id: null, pid_qty: 1 }],
            });
        }
    }, [visible, initialValues, isEditMode]);

    return (
        <Modal
            title={isEditMode ? 'Edit Performa Invoice' : 'Add Performa Invoice'}
            open={visible}
            onCancel={onCancel}
            footer={null}
            width="95%"
            centered
            destroyOnHidden
            maskClosable={false}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    {/* Ledger */}
                    <Col span={8}>
                        <Form.Item
                            name="pi_ledger_id"
                            label="Ledger"
                            rules={[{ required: true, message: 'Select Ledger' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select Ledger"
                                optionFilterProp="label"
                                options={ledgers.map((l) => ({
                                    label: l.ledger_company_name,
                                    value: l.ledger_id,
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    {/* Date */}
                    <Col span={8}>
                        <Form.Item
                            name="pi_date"
                            label="Date"
                            rules={[{ required: true, message: 'Select Date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    {/* Lead Time */}
                    <Col span={8}>
                        <Form.Item
                            name="pi_lead_time"
                            label="Lead Time (Days)"
                            rules={[{ required: true, message: 'Enter lead time' }]}
                        >
                            <Input type="number" min={0} />
                        </Form.Item>
                    </Col>

                    {/* Order No */}
                    <Col span={8}>
                        <Form.Item
                            name="pi_order_no"
                            label="Order No"
                            rules={[{ required: true, message: 'Enter order number' }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                {/* ================= Products ================= */}
                <Form.List name="items">
                    {(fields, { add, remove }) => (
                        <>
                            <div style={{ marginBottom: 10, fontWeight: 600 }}>
                                Products
                            </div>

                            {fields.map(({ key, name, ...restField }) => (
                                <Row gutter={16} key={key} align="middle">
                                    <Col span={12}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'pid_product_id']}
                                            rules={[{ required: true, message: 'Select product' }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Select Product"
                                                optionFilterProp="label"
                                                options={products.map((p) => ({
                                                    label: p.product_name,
                                                    value: p.product_id,
                                                }))}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col span={6}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'pid_qty']}
                                            rules={[
                                                { required: true, message: 'Enter quantity' },
                                                {
                                                    validator: (_, value) =>
                                                        value > 0
                                                            ? Promise.resolve()
                                                            : Promise.reject('Qty must be greater than 0'),
                                                },
                                            ]}
                                        >
                                            <Input type="number" min={1} placeholder="Quantity" />
                                        </Form.Item>
                                    </Col>

                                    <Col span={4}>
                                        <Form.Item>
                                            <Button
                                                danger
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                                disabled={fields.length === 1}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            ))}

                            <Button
                                type="dashed"
                                onClick={() => add({ pid_product_id: null, pid_qty: 1 })}
                                icon={<PlusOutlined />}
                                style={{ width: '100%' }}
                            >
                                Add Product
                            </Button>
                        </>
                    )}
                </Form.List>

                {/* ================= Buttons ================= */}
                <Form.Item style={{ marginTop: 20, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>

                        {!isEditMode && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                            >
                                Submit & Create New
                            </Button>
                        )}

                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PIModel;